import { type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { cn } from './cn.ts';

export type BadgeVariant =
  | 'default'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'purple'
  | 'outline';

const variantClasses: Record<BadgeVariant, string> = {
  default: 'bg-zinc-100 text-zinc-600',
  success: 'bg-emerald-50 text-emerald-700',
  warning: 'bg-amber-50 text-amber-700',
  danger: 'bg-red-50 text-red-600',
  info: 'bg-sky-50 text-sky-700',
  purple: 'bg-violet-50 text-violet-700',
  outline: 'border border-zinc-200 text-zinc-600 bg-transparent',
};

interface BadgeProps {
  variant?: BadgeVariant;
  children: ReactNode;
  className?: string;
  dot?: boolean;
}

export function Badge({ variant = 'default', children, className, dot = false }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium',
        variantClasses[variant],
        className,
      )}
    >
      {dot && (
        <span
          className={cn('w-1.5 h-1.5 rounded-full', {
            'bg-zinc-400': variant === 'default' || variant === 'outline',
            'bg-emerald-500': variant === 'success',
            'bg-amber-500': variant === 'warning',
            'bg-red-500': variant === 'danger',
            'bg-sky-500': variant === 'info',
            'bg-violet-500': variant === 'purple',
          })}
        />
      )}
      {children}
    </span>
  );
}

// Semantic helpers for common statuses
const CAMPAIGN_STATUS_MAP: Record<string, BadgeVariant> = {
  draft: 'warning',
  scheduled: 'info',
  running: 'purple',
  paused: 'warning',
  completed: 'success',
  cancelled: 'default',
};

const TEMPLATE_STATUS_MAP: Record<string, BadgeVariant> = {
  draft: 'warning',
  active: 'success',
  archived: 'default',
};

const AUTOMATION_STATUS_MAP: Record<string, BadgeVariant> = {
  draft: 'warning',
  active: 'success',
  paused: 'warning',
  archived: 'default',
};

const CUSTOMER_STATUS_MAP: Record<string, BadgeVariant> = {
  active: 'success',
  inactive: 'default',
  blocked: 'danger',
  deleted: 'danger',
};

export function CampaignStatusBadge({
  status,
  pauseReason,
}: {
  status: string;
  pauseReason?: string | null;
}) {
  const isAutoPaused = status === 'paused' && pauseReason?.startsWith('auto_');
  if (isAutoPaused) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600">
        <AlertTriangle size={10} className="text-red-500" />
        auto-paused
      </span>
    );
  }
  return (
    <Badge variant={CAMPAIGN_STATUS_MAP[status] ?? 'default'} dot>
      {status}
    </Badge>
  );
}

export function TemplateStatusBadge({ status }: { status: string }) {
  return (
    <Badge variant={TEMPLATE_STATUS_MAP[status] ?? 'default'}>
      {status}
    </Badge>
  );
}

export function AutomationStatusBadge({ status }: { status: string }) {
  return (
    <Badge variant={AUTOMATION_STATUS_MAP[status] ?? 'default'}>
      {status}
    </Badge>
  );
}

export function CustomerStatusBadge({ status }: { status: string }) {
  return (
    <Badge variant={CUSTOMER_STATUS_MAP[status] ?? 'default'} dot>
      {status}
    </Badge>
  );
}

const CHANNEL_MAP: Record<string, { label: string; variant: BadgeVariant }> = {
  email: { label: 'Email', variant: 'info' },
  sms: { label: 'SMS', variant: 'success' },
  push: { label: 'Push', variant: 'purple' },
  webhook: { label: 'Webhook', variant: 'default' },
};

export function ChannelBadge({ channel }: { channel: string }) {
  const { label, variant } = CHANNEL_MAP[channel] ?? { label: channel, variant: 'default' as BadgeVariant };
  return <Badge variant={variant}>{label}</Badge>;
}
